import React, { useState, useEffect } from 'react';
import {
    CurrencyDollarIcon,
    ArrowTrendingUpIcon,
    ArrowTrendingDownIcon,
    ChartBarIcon,
    CalendarIcon,
    BuildingOfficeIcon,
    HomeIcon,
    FunnelIcon,
    MagnifyingGlassIcon,
    EyeIcon
} from '@heroicons/react/24/outline';
import { apiUrl } from '../../utils/api';

interface FinancialCategory {
    _id: string;
    nombre: string;
    tipo: 'ingreso' | 'gasto';
    color: string;
    icono: string;
}

interface Building {
    _id: string;
    nombre: string;
    ciudad: string;
}

interface Property {
    _id: string;
    numero: string;
    tipo: string;
    edificio: string;
}

interface FinancialTransaction {
    _id: string;
    fecha: string;
    tipo: 'ingreso' | 'gasto';
    categoria: FinancialCategory;
    descripcion: string;
    monto: number;
    edificio: Building;
    propiedad: Property;
    numeroReferencia?: string;
    validada: boolean;
    validadaPor?: {
        nombre: string;
        apellido: string;
    };
    fechaValidacion?: string;
    creadoPor: {
        nombre: string;
        apellido: string;
    };
    createdAt: string;
}

interface FinancialSummary {
    totalIngresos: number;
    totalGastos: number;
    balanceNeto: number;
    cantidadTransacciones: number;
    promedioIngreso: number;
    promedioGasto: number;
}

interface CategorySummary {
    _id: string;
    categoria: FinancialCategory;
    totalIngresos: number;
    totalGastos: number;
    balance: number;
    cantidad: number;
}

const FinancialOverview: React.FC = () => {
    const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
    const [summary, setSummary] = useState<FinancialSummary>({
        totalIngresos: 0,
        totalGastos: 0,
        balanceNeto: 0,
        cantidadTransacciones: 0,
        promedioIngreso: 0,
        promedioGasto: 0
    });
    const [categorySummary, setCategorySummary] = useState<CategorySummary[]>([]);
    const [buildings, setBuildings] = useState<Building[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedTransaction, setSelectedTransaction] = useState<FinancialTransaction | null>(null);

    // Filtros
    const [filters, setFilters] = useState({
        search: '',
        tipo: '',
        edificio: '',
        fechaInicio: '',
        fechaFin: ''
    });

    // Paginación
    const [pagination, setPagination] = useState({
        current: 1,
        pages: 1,
        total: 0,
        limit: 20
    });

    useEffect(() => {
        fetchData();
    }, [pagination.current, filters]);

    const fetchData = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            
            // Construir parámetros de consulta
            const params = new URLSearchParams();
            params.append('page', pagination.current.toString());
            params.append('limit', pagination.limit.toString());
            
            if (filters.search) params.append('search', filters.search);
            if (filters.tipo) params.append('tipo', filters.tipo);
            if (filters.edificio) params.append('edificio', filters.edificio);
            if (filters.fechaInicio) params.append('fechaInicio', filters.fechaInicio);
            if (filters.fechaFin) params.append('fechaFin', filters.fechaFin);

            const [transactionsRes, summaryRes, buildingsRes] = await Promise.all([
                fetch(apiUrl(`/api/financial/transactions?${params}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(apiUrl(`/api/financial/transactions/summary?${params}`), {
                    headers: { 'Authorization': `Bearer ${token}` }
                }),
                fetch(apiUrl('/api/buildings'), {
                    headers: { 'Authorization': `Bearer ${token}` }
                })
            ]);

            if (transactionsRes.ok) {
                const transactionsData = await transactionsRes.json();
                if (transactionsData.success && transactionsData.data) {
                    setTransactions(Array.isArray(transactionsData.data.transactions) ? transactionsData.data.transactions : []);
                    setPagination(transactionsData.data.pagination || { current: 1, pages: 1, total: 0, limit: 20 });
                } else {
                    console.error('Error: transactions data is invalid:', transactionsData);
                    setTransactions([]);
                }
            } else {
                console.error('Error fetching transactions:', transactionsRes.status, transactionsRes.statusText);
                setTransactions([]);
            }

            if (summaryRes.ok) {
                const summaryData = await summaryRes.json();
                if (summaryData.success && summaryData.data) {
                    setSummary(summaryData.data.summary || {
                        totalIngresos: 0,
                        totalGastos: 0,
                        balanceNeto: 0,
                        cantidadTransacciones: 0,
                        promedioIngreso: 0,
                        promedioGasto: 0
                    });
                    setCategorySummary(Array.isArray(summaryData.data.categorySummary) ? summaryData.data.categorySummary : []);
                } else {
                    console.error('Error: summary data is invalid:', summaryData);
                }
            } else {
                console.error('Error fetching summary:', summaryRes.status, summaryRes.statusText);
            }

            if (buildingsRes.ok) {
                const buildingsData = await buildingsRes.json();
                if (buildingsData.success && buildingsData.data) {
                    // La API devuelve data.buildings, no data directamente
                    const buildingsArray = buildingsData.data.buildings || buildingsData.data;
                    if (Array.isArray(buildingsArray)) {
                        setBuildings(buildingsArray);
                    } else {
                        console.error('Error: buildings data is not an array:', buildingsData);
                        setBuildings([]);
                    }
                } else {
                    console.error('Error: buildings data is invalid:', buildingsData);
                    setBuildings([]);
                }
            } else {
                console.error('Error fetching buildings:', buildingsRes.status, buildingsRes.statusText);
                setBuildings([]);
            }

        } catch (error) {
            console.error('Error al cargar datos financieros:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('es-ES', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES');
    };

    const getTipoColor = (tipo: string) => {
        return tipo === 'ingreso' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    };

    const getTipoLabel = (tipo: string) => {
        return tipo === 'ingreso' ? 'Ingreso' : 'Gasto';
    };

    const getBalanceColor = (balance: number) => {
        if (balance > 0) return 'text-green-600';
        if (balance < 0) return 'text-red-600';
        return 'text-gray-600';
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-hotel-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-2">Portal Inversionistas</h1>
                <h2 className="text-2xl font-semibold text-gray-600">Estado Financiero</h2>
                <p className="text-gray-600">Consulta los ingresos y gastos de tus propiedades</p>
            </div>

            {/* Resumen Financiero */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ArrowTrendingUpIcon className="h-8 w-8 text-green-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Ingresos</p>
                            <p className="text-2xl font-semibold text-green-600">
                                {formatCurrency(summary.totalIngresos)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ArrowTrendingDownIcon className="h-8 w-8 text-red-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Total Gastos</p>
                            <p className="text-2xl font-semibold text-red-600">
                                {formatCurrency(summary.totalGastos)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <CurrencyDollarIcon className="h-8 w-8 text-blue-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Balance Neto</p>
                            <p className={`text-2xl font-semibold ${getBalanceColor(summary.balanceNeto)}`}>
                                {formatCurrency(summary.balanceNeto)}
                            </p>
                        </div>
                    </div>
                </div>

                <div className="bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center">
                        <div className="flex-shrink-0">
                            <ChartBarIcon className="h-8 w-8 text-purple-600" />
                        </div>
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-500">Transacciones</p>
                            <p className="text-2xl font-semibold text-gray-900">
                                {summary.cantidadTransacciones}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Resumen por Categoría */}
            {categorySummary.length > 0 && (
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-medium text-gray-900">Resumen por Categoría</h3>
                    </div>
                    <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {Array.isArray(categorySummary) && categorySummary.map((item) => (
                                <div key={item._id} className="border rounded-lg p-4">
                                    <div className="flex items-center space-x-3 mb-2">
                                        <div 
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: item.categoria.color }}
                                        ></div>
                                        <h4 className="font-medium text-gray-900">{item.categoria.nombre}</h4>
                                    </div>
                                    <div className="space-y-1 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Ingresos:</span>
                                            <span className="text-green-600 font-medium">
                                                {formatCurrency(item.totalIngresos)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Gastos:</span>
                                            <span className="text-red-600 font-medium">
                                                {formatCurrency(item.totalGastos)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between border-t pt-1">
                                            <span className="text-gray-500">Balance:</span>
                                            <span className={`font-medium ${getBalanceColor(item.balance)}`}>
                                                {formatCurrency(item.balance)}
                                            </span>
                                        </div>
                                        <div className="text-xs text-gray-400">
                                            {item.cantidad} transacciones
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Filtros */}
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Buscar
                        </label>
                        <div className="relative">
                            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={filters.search}
                                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                                className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                                placeholder="Buscar transacciones..."
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Tipo
                        </label>
                        <select
                            value={filters.tipo}
                            onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        >
                            <option value="">Todos los tipos</option>
                            <option value="ingreso">Ingresos</option>
                            <option value="gasto">Gastos</option>
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Edificio
                        </label>
                        <select
                            value={filters.edificio}
                            onChange={(e) => setFilters({ ...filters, edificio: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        >
                            <option value="">Todos los edificios</option>
                            {Array.isArray(buildings) && buildings.map((building) => (
                                <option key={building._id} value={building._id}>
                                    {building.nombre} - {building.ciudad}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Fecha Inicio
                        </label>
                        <input
                            type="date"
                            value={filters.fechaInicio}
                            onChange={(e) => setFilters({ ...filters, fechaInicio: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                        />
                    </div>
                </div>

                <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                        Fecha Fin
                    </label>
                    <input
                        type="date"
                        value={filters.fechaFin}
                        onChange={(e) => setFilters({ ...filters, fechaFin: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-hotel-500 focus:border-transparent"
                    />
                </div>
            </div>

            {/* Tabla de transacciones */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-medium text-gray-900">Transacciones</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Categoría
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Descripción
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Monto
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Propiedad
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Estado
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Array.isArray(transactions) && transactions.map((transaction) => (
                                <tr key={transaction._id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {formatDate(transaction.fecha)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoColor(transaction.tipo)}`}>
                                            {getTipoLabel(transaction.tipo)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center space-x-2">
                                            <div 
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: transaction.categoria.color }}
                                            ></div>
                                            <span className="text-sm text-gray-900">{transaction.categoria.nombre}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                        {transaction.descripcion}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <span className={transaction.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                                            {transaction.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(transaction.monto)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        <div>
                                            <div className="font-medium">{transaction.propiedad.numero}</div>
                                            <div className="text-gray-500">{transaction.edificio.nombre}</div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {transaction.validada ? (
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-green-800 bg-green-100">
                                                Validada
                                            </span>
                                        ) : (
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-yellow-800 bg-yellow-100">
                                                Pendiente
                                            </span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => setSelectedTransaction(transaction)}
                                            className="text-hotel-600 hover:text-hotel-900"
                                            title="Ver detalles"
                                        >
                                            <EyeIcon className="h-4 w-4" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Paginación */}
                {pagination.pages > 1 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setPagination({ ...pagination, current: Math.max(1, pagination.current - 1) })}
                                disabled={pagination.current === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Anterior
                            </button>
                            <button
                                onClick={() => setPagination({ ...pagination, current: Math.min(pagination.pages, pagination.current + 1) })}
                                disabled={pagination.current === pagination.pages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                            >
                                Siguiente
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Mostrando{' '}
                                    <span className="font-medium">{(pagination.current - 1) * pagination.limit + 1}</span>
                                    {' '}a{' '}
                                    <span className="font-medium">
                                        {Math.min(pagination.current * pagination.limit, pagination.total)}
                                    </span>
                                    {' '}de{' '}
                                    <span className="font-medium">{pagination.total}</span>
                                    {' '}resultados
                                </p>
                            </div>
                            <div>
                                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                    <button
                                        onClick={() => setPagination({ ...pagination, current: Math.max(1, pagination.current - 1) })}
                                        disabled={pagination.current === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Anterior
                                    </button>
                                    {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                                        const page = i + 1;
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setPagination({ ...pagination, current: page })}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                                                    pagination.current === page
                                                        ? 'z-10 bg-hotel-50 border-hotel-500 text-hotel-600'
                                                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    })}
                                    <button
                                        onClick={() => setPagination({ ...pagination, current: Math.min(pagination.pages, pagination.current + 1) })}
                                        disabled={pagination.current === pagination.pages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        Siguiente
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal de detalles de transacción */}
            {selectedTransaction && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-1/2 shadow-lg rounded-md bg-white">
                        <div className="mt-3">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Detalles de la Transacción</h3>
                                <button
                                    onClick={() => setSelectedTransaction(null)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">Fecha</label>
                                        <p className="text-sm text-gray-900">{formatDate(selectedTransaction.fecha)}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">Tipo</label>
                                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTipoColor(selectedTransaction.tipo)}`}>
                                            {getTipoLabel(selectedTransaction.tipo)}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Categoría</label>
                                    <div className="flex items-center space-x-2">
                                        <div 
                                            className="w-4 h-4 rounded-full"
                                            style={{ backgroundColor: selectedTransaction.categoria.color }}
                                        ></div>
                                        <p className="text-sm text-gray-900">{selectedTransaction.categoria.nombre}</p>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Descripción</label>
                                    <p className="text-sm text-gray-900">{selectedTransaction.descripcion}</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Monto</label>
                                    <p className={`text-lg font-semibold ${selectedTransaction.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}`}>
                                        {selectedTransaction.tipo === 'ingreso' ? '+' : '-'}{formatCurrency(selectedTransaction.monto)}
                                    </p>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">Propiedad</label>
                                        <p className="text-sm text-gray-900">{selectedTransaction.propiedad.numero}</p>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">Edificio</label>
                                        <p className="text-sm text-gray-900">{selectedTransaction.edificio.nombre}</p>
                                    </div>
                                </div>

                                {selectedTransaction.numeroReferencia && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-500">Número de Referencia</label>
                                        <p className="text-sm text-gray-900">{selectedTransaction.numeroReferencia}</p>
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Estado</label>
                                    {selectedTransaction.validada ? (
                                        <div>
                                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-green-800 bg-green-100">
                                                Validada
                                            </span>
                                            {selectedTransaction.validadaPor && (
                                                <p className="text-xs text-gray-500 mt-1">
                                                    Por: {selectedTransaction.validadaPor.nombre} {selectedTransaction.validadaPor.apellido}
                                                </p>
                                            )}
                                            {selectedTransaction.fechaValidacion && (
                                                <p className="text-xs text-gray-500">
                                                    El: {formatDate(selectedTransaction.fechaValidacion)}
                                                </p>
                                            )}
                                        </div>
                                    ) : (
                                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-yellow-800 bg-yellow-100">
                                            Pendiente de validación
                                        </span>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-500">Creado por</label>
                                    <p className="text-sm text-gray-900">
                                        {selectedTransaction.creadoPor.nombre} {selectedTransaction.creadoPor.apellido}
                                    </p>
                                </div>
                            </div>

                            <div className="flex justify-end pt-4">
                                <button
                                    onClick={() => setSelectedTransaction(null)}
                                    className="px-4 py-2 bg-hotel-600 text-white rounded-lg hover:bg-hotel-700"
                                >
                                    Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FinancialOverview;
